import { useEffect, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { Box, Button, LoadingOverlay, Select, Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';

import { UiCard } from '../../components/ui/ui-card/ui-card';
import { apiClient } from '../../core/libs/api';
import { HttpResource } from '../../core/libs/http-resource';
import { AppNotify } from '../../features/app-notify/app-notify';
import { routeOrderStatus } from '../../router/routes';
import { UserTimeZoneNotify } from '../tz-notify/tz-notify';
import { AddPassengers } from './add-passengers';
import { MyPassenger } from './add-passengers/types';
import { Agreement } from './agreement';
import { PayConfirm } from './pay-confirm';
import { RedirectToBank } from './redirect-to-bank';
import { addOrderRequest } from './requests/add_order.request';
import { bookPaySeatRequest } from './requests/book_pay_seat.request';
import { SelectSeats } from './select-seats';
import { TripInfo } from './trip-info';
import { CreatedPayment, NewBookSeat, NewTicketPayment } from './types';
import { useTripData } from './use-payment-data';

type Steps =
  | 'addPassengers'
  | 'selectSeats'
  | 'applyAgreement'
  | 'confirmPay'
  | 'redirectToBank';

type OrderTicketProps = {
  tripid: string;
  startStationId: string;
  endStationId: string;
  onChangeStep: (step: Steps) => void;
};

export const BuyTicket = observer(
  ({
    tripid,
    startStationId,
    endStationId,
    onChangeStep,
  }: OrderTicketProps) => {
    const [step, setStep] = useState<Steps>('addPassengers');

    const [passengers, setPassengers] = useState<MyPassenger[]>([]);
    const [seats, setSeats] = useState<{ wagonNum: number; seatNum: number }[]>(
      []
    );

    const handleChangeStep = (step: Steps) => {
      setStep(step);
      onChangeStep(step);
    };

    const [newPay, setNewPay] = useState<CreatedPayment>({
      date: new Date().toJSON(),
      description: '',
      id: 0,

      payCheckNumber: 0,

      paySumm: 0,
      payConfirmationStatus: 0,
      payConfirmationUrl: '',
      payId: null,
      payRemoteId: '',
      payReturnUrl: '',
      stationZoneId: 0,
      payTerminalId: 0,
      ticketItems: [],
      payTicketQr: '',
      tripId: 0,
      payType: 1,
      personId: 0,
      shared: false,
      type: 100,
    });

    const [{ startStation, endStation, trip, tariffs }, tripRequest] =
      useTripData({
        startStationId,
        endStationId,
      });

    useEffect(() => {
      tripRequest.send(tripid || '-1');
    }, [tripRequest, tripid]);

    const [notify] = useState(() => new AppNotify());

    const [createPaymentRequest] = useState(
      () =>
        new HttpResource<NewTicketPayment, []>({
          apiClient,
          httpRequest: addOrderRequest,
          errorNotify: notify,
          onFinish(result: any) {
            const nextStep = 'redirectToBank';

            handleChangeStep(nextStep);
            setNewPay(result);
          },
        })
    );

    const [bookSeatsRequest] = useState(
      () =>
        new HttpResource<NewBookSeat, []>({
          apiClient,
          httpRequest: bookPaySeatRequest,
          errorNotify: notify,
          onFinish(result: any) {
            handleChangeStep('confirmPay');
            setNewPay(result);
          },
        })
    );

    const handleCreatePayment = () => {
      const payReturnUrl = new URL(
        routeOrderStatus.path,
        window.location.origin
      ).toString();

      const createdPaymentData =
        seats.length === 0
          ? {
              payReturnUrl: payReturnUrl,
              stationToId: endStation?.id,
              stationFromId: startStation?.id,
              // tickets: createTickets(passengers[0].tariffs),
              tickets: passengers.flatMap((pass) =>
                pass.tariffs.map((tariff) => ({
                  tariffId: tariff.id,
                }))
              ),
              // tariffs.map(({ id }) => ({
              //   tariffId: id,
              // })),
              tripId: Number(tripid),
              payType: 1,
            }
          : newPay;

      // @ts-expect-error just types
      createPaymentRequest.send(createdPaymentData);
    };

    const saveSeats = (
      seats: {
        wagonNum: number;
        seatNum: number;
      }[]
    ) => {
      console.log({ passengers, seats });

      setSeats(seats);
      setStep('applyAgreement');
    };

    const handleBookSeats = () => {
      const payReturnUrl = new URL(
        routeOrderStatus.path,
        window.location.origin
      ).toString();

      const order: NewBookSeat = {
        payReturnUrl,
        payType: 1,
        tripId: Number(tripid),
        stationToId: endStation?.id || 0,
        stationFromId: startStation?.id || 0,
        // get all passengers tariffs and add wagonNum and seatNum by index
        tickets: passengers.flatMap((pass, idx) =>
          pass.tariffs.map((tariff) => ({
            tariffId: tariff.id,
            wagonNum: tariff.main ? seats[idx].wagonNum : undefined,
            seatNum: tariff.main ? seats[idx].seatNum : undefined,
          }))
        ),
      };

      bookSeatsRequest.send(order);
    };

    const handleSelectSeats = () => {
      setStep('selectSeats');
    };

    const isSelectSeatRequired = trip.wagons && trip.wagons.length > 0;

    return (
      <Box pos="relative" mih="150px">
        <LoadingOverlay
          visible={
            tripRequest.isWaitingForLoad ||
            createPaymentRequest.isLoading ||
            bookSeatsRequest.isLoading
          }
          overlayBlur={2}
        />

        {!tripRequest.isWaitingForLoad && (
          <>
            {step === 'addPassengers' && (
              <Stack spacing="sm">
                <UserTimeZoneNotify />

                <TripInfo
                  trip={trip}
                  startStation={startStation}
                  endStation={endStation}
                />

                <AddPassengers
                  tariffs={tariffs}
                  tripStartTime={trip.startTime}
                  onChange={setPassengers}
                />

                {!isSelectSeatRequired && (
                  <UiCard>
                    <Stack spacing="sm">
                      <Select
                        value={t`Банковской картой`}
                        data={[t`Банковской картой`]}
                      />
                      {/* <Button size="lg" onClick={handleCreatePayment}> */}
                      <Button
                        size="lg"
                        onClick={() => setStep('applyAgreement')}
                      >
                        <Trans>Продолжить</Trans>
                      </Button>
                    </Stack>
                  </UiCard>
                )}

                {isSelectSeatRequired && (
                  <UiCard>
                    <Button size="lg" onClick={handleSelectSeats} fullWidth>
                      <Trans>Выбрать место</Trans>
                    </Button>
                  </UiCard>
                )}
              </Stack>
            )}

            {step === 'selectSeats' && (
              <Stack spacing="sm">
                <TripInfo
                  trip={trip}
                  startStation={startStation}
                  endStation={endStation}
                />
                <SelectSeats
                  key="4242"
                  wagons={trip.wagons}
                  passengers={passengers}
                  onSubmit={saveSeats}
                />
                <UiCard>
                  <Button
                    variant="outline"
                    size="md"
                    fullWidth
                    onClick={() => setStep('addPassengers')}
                  >
                    <Trans>Изменить данные пассажиров</Trans>
                  </Button>
                </UiCard>
              </Stack>
            )}

            {step === 'applyAgreement' && (
              <Stack spacing="sm">
                <Agreement route={trip.route} />
                <UiCard>
                  <Stack spacing="sm">
                    <Button
                      size="lg"
                      fullWidth
                      onClick={() =>
                        isSelectSeatRequired
                          ? handleBookSeats()
                          : handleCreatePayment()
                      }
                    >
                      <Trans>Оплатить</Trans>
                    </Button>
                    <Button
                      variant="outline"
                      size="md"
                      fullWidth
                      onClick={() => setStep('addPassengers')}
                    >
                      <Trans>Изменить данные</Trans>
                    </Button>
                  </Stack>
                </UiCard>
              </Stack>
            )}

            {step === 'confirmPay' && (
              <Stack spacing="sm">
                <PayConfirm
                  payment={Array.isArray(newPay) ? newPay[0] : newPay}
                  stations={{ startStation, endStation }}
                />
                <UiCard>
                  <Stack spacing="sm">
                    <Button
                      size="lg"
                      fullWidth
                      onClick={() => handleCreatePayment()}
                    >
                      <Trans>Оплатить</Trans>
                    </Button>
                    <Button
                      variant="outline"
                      size="md"
                      fullWidth
                      onClick={() => setStep('addPassengers')}
                    >
                      <Trans>Изменить данные</Trans>
                    </Button>
                  </Stack>
                </UiCard>
              </Stack>
            )}

            {step === 'redirectToBank' && (
              <RedirectToBank url={newPay.payConfirmationUrl} />
            )}
          </>
        )}
      </Box>
    );
  }
);
